import { Box, Chip, Typography, makeStyles } from '@material-ui/core';
import { InfoCard, Link, Progress } from '@backstage/core-components';
import { CatalogAudit, entityPagePath, useCatalogAudit } from './api';

const useStyles = makeStyles(theme => ({
  counters: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
  },
  entity: {
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': { borderBottom: 'none' },
  },
  title: { fontWeight: 600 },
  finding: { marginLeft: theme.spacing(2) },
  muted: { color: theme.palette.text.secondary },
}));

const PROBLEM_COLOR = '#F2A900';
const COVERAGE_COLOR = '#8A8FA8';

const EntityRows = ({
  audit,
  severity,
}: {
  audit: CatalogAudit;
  severity: 'problem' | 'coverage';
}) => {
  const classes = useStyles();
  const rows = audit.entities
    .map(e => ({
      ...e,
      findings: e.findings.filter(f => f.severity === severity),
    }))
    .filter(e => e.findings.length);
  return (
    <>
      {rows.map(e => (
        <div key={e.entityRef} className={classes.entity}>
          <Link to={entityPagePath(e.entityRef)} className={classes.title}>
            {e.title ?? e.entityRef.split('/').pop()}
          </Link>
          {e.findings.map((f, i) => (
            <Typography key={i} variant="body2" className={classes.finding}>
              <span
                style={{
                  color:
                    severity === 'problem' ? PROBLEM_COLOR : COVERAGE_COLOR,
                  fontWeight: 700,
                }}
              >
                {severity === 'problem' ? '▲' : '•'}
              </span>{' '}
              {f.message}
            </Typography>
          ))}
        </div>
      ))}
    </>
  );
};

/**
 * Актуальность каталога: висячие связи, неработающие селекторы, битые ссылки (проблемы) и сервисы
 * без логов, дашбордов или алертов (покрытие). Проверка ночная, данные плагина kubernetes-diagnostics.
 */
export const CatalogAuditCard = () => {
  const classes = useStyles();
  const { value, error, loading } = useCatalogAudit();
  return (
    <InfoCard
      title="Актуальность каталога"
      subheader={
        value
          ? `Проверено сущностей: ${value.checkedEntities}, ${new Date(
              value.generatedAt,
            ).toLocaleString()}`
          : undefined
      }
    >
      {loading && !value && <Progress />}
      {error && !value && (
        <Typography color="error">{error.message}</Typography>
      )}
      {value && (
        <>
          <Box className={classes.counters}>
            <Chip
              size="small"
              label={`проблемы: ${value.summary.problems}`}
              style={{
                background: value.summary.problems ? PROBLEM_COLOR : '#06AA2A',
                color: '#fff',
              }}
            />
            <Chip
              size="small"
              label={`пробелы в наблюдении: ${value.summary.coverage}`}
              style={{ background: COVERAGE_COLOR, color: '#fff' }}
            />
          </Box>
          {value.errors.map(e => (
            <Typography key={e} color="error" variant="body2">
              Не удалось проверить: {e}
            </Typography>
          ))}
          {value.summary.problems === 0 ? (
            <Typography className={classes.muted}>
              Проблем в каталоге нет.
            </Typography>
          ) : (
            <EntityRows audit={value} severity="problem" />
          )}
          {value.summary.coverage > 0 && (
            <>
              <Typography variant="subtitle2" style={{ marginTop: 16 }}>
                Пробелы в наблюдении
              </Typography>
              <EntityRows audit={value} severity="coverage" />
            </>
          )}
        </>
      )}
    </InfoCard>
  );
};
